import { Switch } from '@/components/ui/switch';
import { ArrowDown, ArrowRight, Check, Gift } from 'lucide-react';
import { pricingPlans } from './pricingData';

interface Props {
  isAnnual: boolean;
  setIsAnnual: (v: boolean) => void;
  hasReferralDiscount: boolean;
  onGetStarted: (planId: string) => void;
}

const NAMES: Record<string, string> = { economy: 'Economy', premium_economy: 'Premium Economy', first_class: 'First Class' };
const CTAS: Record<string, string> = { economy: 'Start free', premium_economy: 'Choose Premium Economy', first_class: 'Choose First Class' };

const JUMPS = [
  { href: '#compare-plans', label: 'Compare all plan features' },
  { href: '#gift-subscriptions', label: 'Gift a subscription' },
  { href: '#pricing-faq', label: 'Help & FAQ' },
];

export const PricingPlansSection = ({ isAnnual, setIsAnnual, hasReferralDiscount, onGetStarted }: Props) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 border-t border-border" aria-labelledby="plans-heading">
    {hasReferralDiscount && (
      <div className="mb-8 flex items-center gap-3 border border-primary/30 bg-primary/5 p-4">
        <Gift className="h-5 w-5 text-primary shrink-0" />
        <p className="text-sm text-foreground"><strong>Referral discount active:</strong> 10% off your first subscription.</p>
      </div>
    )}

    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 mb-8">
      <h2 id="plans-heading" className="text-sm font-bold uppercase tracking-wide text-primary">Regular travel · Subscriptions</h2>
      <div className="flex flex-wrap items-center gap-3">
        <span className={`text-base ${!isAnnual ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>Monthly</span>
        <label className="inline-flex min-h-12 min-w-12 items-center justify-center cursor-pointer"><span className="sr-only">Bill annually</span><Switch checked={isAnnual} onCheckedChange={setIsAnnual} aria-label="Bill annually" /></label>
        <span className={`text-base ${isAnnual ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>Annual</span>
        <span className="text-sm font-semibold text-primary">Save 20%</span>
      </div>
    </div>

    <div className="flex flex-wrap gap-x-4 sm:gap-x-10 gap-y-3 mb-12">
      {JUMPS.map((j) => (
        <a key={j.href} href={j.href} className="inline-flex items-center gap-2 min-h-[44px] text-base text-primary underline underline-offset-4 hover:opacity-80">
          {j.label} <ArrowDown className="h-4 w-4" />
        </a>
      ))}
    </div>

    <div className="grid md:grid-cols-3 gap-12 md:gap-10 lg:gap-16">
      {pricingPlans.map((plan) => {
        const isFree = plan.id === 'economy';
        const featured = plan.id === 'premium_economy';
        const price = isFree ? 'Free' : isAnnual ? plan.annualPrice : plan.monthlyPrice;
        return (
          <div key={plan.id} className="flex flex-col">
            <h3 className="text-2xl font-bold text-foreground">{NAMES[plan.id]}</h3>
            <p className="mt-3 text-base text-muted-foreground">{plan.description}</p>
            <div className="mt-6 min-h-[96px]">
              <p className="flex items-baseline gap-3">
                <span className="text-5xl font-bold text-foreground">{price}</span>
                {!isFree && <span className="text-base text-muted-foreground">{isAnnual ? '/year' : '/month'}</span>}
              </p>
              {!isFree && isAnnual && plan.annualMonthlyEquivalent && (
                <p className="mt-2 text-sm text-primary">{plan.annualMonthlyEquivalent}/month billed yearly</p>
              )}
            </div>
            <button
              onClick={() => onGetStarted(plan.id)}
              className={`mt-6 inline-flex items-center justify-center gap-3 min-h-[60px] rounded border text-base font-bold transition-colors ${
                featured
                  ? 'bg-primary border-primary text-primary-foreground hover:bg-primary/90'
                  : 'border-border text-foreground hover:border-primary hover:text-primary'
              }`}
            >
              {CTAS[plan.id]} <ArrowRight className="h-4 w-4" />
            </button>
            <ul className="mt-8 space-y-4">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-base text-muted-foreground">
                  <Check className="h-4 w-4 mt-1 shrink-0 text-primary" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  </section>
);
